import {useIsOverviewEnabled} from '../hooks/use-feature-state'
import {ContextualCommentControlsProvider} from '../components/comments/CommentsContext'
import {Outlet} from '@github-ui/react-router'
import {pullRequestsLayoutRoute} from './layout-route'
import {useRespondToRouteChange} from '../hooks/use-respond-to-route-change'
import {useCallback, useRef, type PropsWithChildren} from 'react'
import {PageDataContextProvider} from '@github-ui/pull-request-page-data-tooling/page-data-context'
import {useEnabledDockedPanelIds} from '../components/toolbar/panel-registry'
import {DockedPanelProvider} from '../contexts/DockedPanelContext'
import {
  condensedResponsiveWrapperClasses,
  fullWidthResponsiveWrapperClasses,
} from '../components/StickyPullRequestHeader'
import {useActiveRoute} from '../hooks/use-active-route'
import {getRouteConfig} from './route-manager'
import {DiffComparisonViewer} from '../components/DiffComparisonViewer'
import {UserNameDisplayContextProvider} from '@github-ui/profile-reference/UserNameDisplayContext'
import {PullRequestHeader} from '../components/PullRequestHeader'
import {PullRequestMiniHeader} from '../components/PullRequestMiniHeader'
import type {AliveChannels} from '../page-data/payloads/header'
import {useRespondToAliveUpdate} from '../hooks/use-refetch-on-alive-update'
import {LayoutStoreProvider} from '../stores/LayoutStore'
import {FilesChangedDataCapture, LayoutStoreSeeder} from '../components/LayoutStoreSeeder'
import {BatchOutdatedRevalidator} from '../components/BatchOutdatedRevalidator'
import type {FilesRoutePayload} from '../page-data/payloads/files'
import {useHeaderLiveUpdates} from '../hooks/use-header-live-updates'
import {useDescriptionLiveUpdates} from '../hooks/use-description-live-updates'
import {useTabCountLiveUpdates} from '../hooks/use-tab-count-live-updates'
import {useFooterBorder} from '@github-ui/layout-primitives/use-footer-border'
import {MergeMethodContextProvider, UnknownMergeabilityTelemetryProvider} from '@github-ui/mergebox'
import {useMergeStatusButtonData} from '../hooks/use-merge-status-button-data'
import {useRouteQueryKey} from '@github-ui/react-core/future/use-route-query-key'
import {useQueryClient} from '@github-ui/react-query'
import {useTimelineLiveUpdates} from '../hooks/use-timeline-live-updates'
import {useActivityLiveUpdates} from '../hooks/use-activity-live-updates'
import {CopilotReviewEffortProvider} from '../components/overview/reviewers/copilot-review-effort/CopilotReviewEffortProvider'
import {useCopilotDockedPanel} from '../hooks/use-copilot-docked-panel'
import {useStickyHeaderNavigation} from '../hooks/use-sticky-header-navigation'
import {useLayoutQuery} from '../page-data/loaders/use-layout-query'

function CopilotDockedPanelIntegration({isOnSupportedRoute}: {isOnSupportedRoute: boolean}) {
  const activeRoute = useActiveRoute()
  const isOverviewEnabled = useIsOverviewEnabled()
  useCopilotDockedPanel({isOnSupportedRoute, activeRoute, isOverviewEnabled})
  return null
}

export function LayoutEntrypoint() {
  const {pullRequest, urls, aliveChannels} = useLayoutQuery().data

  const queryClient = useQueryClient()
  const diffStatQueryKey = useRouteQueryKey(pullRequestsLayoutRoute, 'diffstat')
  const tabCountsQueryKey = useRouteQueryKey(pullRequestsLayoutRoute, 'tabCounts')

  const needUpdateRef = useRef(false)

  const markNeedsUpdate = useCallback(() => {
    needUpdateRef.current = true
  }, [])

  useRespondToAliveUpdate(aliveChannels.pullRequest, markNeedsUpdate, {git_updated: true, timeline_updated: true})

  // Refresh shared data when the route changes to keep diffstat and tab counts up to date.
  // We only invalidate on navigation to avoid excessive refetching.
  const handleRouteChange = useCallback(() => {
    if (!needUpdateRef.current) return

    needUpdateRef.current = false
    queryClient.invalidateQueries({queryKey: diffStatQueryKey, exact: true})
    queryClient.invalidateQueries({queryKey: tabCountsQueryKey, exact: true})
  }, [queryClient, diffStatQueryKey, tabCountsQueryKey])

  useRespondToRouteChange(handleRouteChange)

  return (
    <PageDataContextProvider basePageDataUrl={urls.conversation}>
      <LayoutComponent
        /* trigger a full remount when PR changes */
        key={pullRequest.id}
      >
        <Outlet />
      </LayoutComponent>
    </PageDataContextProvider>
  )
}

function HeaderLiveUpdates({aliveChannels}: {aliveChannels: AliveChannels}) {
  useHeaderLiveUpdates(aliveChannels)
  return null
}

// Subscribes once for the whole layout so multiple `useDescription` consumers
// (overview panel + toolbar button) share a single alive handler and trigger
// at most one refetch per real change. Must render under PageDataContextProvider.
function DescriptionLiveUpdates({aliveChannels}: {aliveChannels: AliveChannels}) {
  useDescriptionLiveUpdates(aliveChannels.pullRequest)
  return null
}

function TimelineLiveUpdates({channel}: {channel: string | null | undefined}) {
  useTimelineLiveUpdates(channel)
  useActivityLiveUpdates(channel)
  return null
}

export function LayoutComponent({children}: PropsWithChildren) {
  const {
    aliveChannels,
    pullRequest,
    repository,
    urls,
    user,
    mergeStatusButtonData,
    viewSettings,
    viewerPendingReview,
    commitMessageGenerationConfig,
  } = useLayoutQuery().data
  const activeRoute = useActiveRoute()
  const showUpdatedHeader = useIsOverviewEnabled()
  const {layout, dockedPanelSupported} = getRouteConfig(activeRoute, showUpdatedHeader)
  const isFullWidth = layout === 'full-width'
  const responsiveWrapperClasses = isFullWidth ? fullWidthResponsiveWrapperClasses : condensedResponsiveWrapperClasses
  const enabledPanels = useEnabledDockedPanelIds()
  const {boundaryRef, navigationRef, showCompactNavigation} = useStickyHeaderNavigation(showUpdatedHeader)

  const basePath = pullRequest.pathName ?? urls.conversation
  const initialFilesDataRef = useRef<FilesRoutePayload | undefined>(undefined)
  const seededRef = useRef(false)

  // Hoisted MergeMethodContextProvider so the in-header merge status trigger
  // and the centrally-rendered MergeStatusPanel share a single mergeMethod
  // state. The provider must live inside `PageDataContextProvider` because
  // its invalidation effect depends on `useMergeBoxPageDataQueryKey`. We key
  // the provider on the PR id so a soft navigation between PRs resets the
  // selected merge method instead of carrying it over.
  const validatedMergeData = useMergeStatusButtonData(pullRequest.state, mergeStatusButtonData)

  useFooterBorder(showUpdatedHeader)

  useTabCountLiveUpdates()

  return (
    <ContextualCommentControlsProvider>
      <FilesChangedDataCapture dataRef={initialFilesDataRef} />
      <LayoutStoreProvider
        initData={{pullRequest, repository, user, viewerPendingReview, commitMessageGenerationConfig}}
        basePath={basePath}
        initialFilesDataRef={initialFilesDataRef}
        seededRef={seededRef}
      >
        <LayoutStoreSeeder seededRef={seededRef} />
        <BatchOutdatedRevalidator />
        <TimelineLiveUpdates channel={aliveChannels.pullRequest} />
        <DockedPanelProvider isOnSupportedRoute={dockedPanelSupported} enabledPanels={enabledPanels}>
          <CopilotDockedPanelIntegration isOnSupportedRoute={dockedPanelSupported} />
          <CopilotReviewEffortProvider>
            <MergeMethodContextProvider
              key={pullRequest.id}
              defaultMergeMethod={validatedMergeData?.defaultMergeMethod ?? undefined}
            >
              <UnknownMergeabilityTelemetryProvider>
                <UserNameDisplayContextProvider value={repository.userNameDisplayConfiguration || undefined}>
                  <DiffComparisonViewer.Container
                    stickyHeaderBoundaryRef={showUpdatedHeader ? boundaryRef : undefined}
                    className={showUpdatedHeader ? responsiveWrapperClasses : `tmp-mt-4 ${responsiveWrapperClasses}`}
                    dockedPanelWidth={viewSettings.dockedPanelWidth}
                    // Matches dotcom's `hovercard-subject-tag`, which a React navigation to another pull request
                    // doesn't replace.
                    hovercardSubjectTag={`pull_request:${pullRequest.id}`}
                    owner={repository.ownerLogin}
                    repo={repository.name}
                    pullNumber={pullRequest.number}
                    stickyHeader={
                      showUpdatedHeader ? (
                        <PullRequestMiniHeader
                          showCompactNavigation={showCompactNavigation}
                          activeRoute={activeRoute}
                        />
                      ) : undefined
                    }
                  >
                    <DiffComparisonViewer.Header divider="none" padding="none">
                      <PullRequestHeader
                        navigationRef={showUpdatedHeader ? navigationRef : undefined}
                        activeRoute={activeRoute}
                        stickyHeaderVariant={layout}
                      />
                    </DiffComparisonViewer.Header>
                    {children}
                  </DiffComparisonViewer.Container>
                  <HeaderLiveUpdates aliveChannels={aliveChannels} />
                  <DescriptionLiveUpdates aliveChannels={aliveChannels} />
                </UserNameDisplayContextProvider>
              </UnknownMergeabilityTelemetryProvider>
            </MergeMethodContextProvider>
          </CopilotReviewEffortProvider>
        </DockedPanelProvider>
      </LayoutStoreProvider>
    </ContextualCommentControlsProvider>
  )
}
